import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/axios';
import { formatWeekRange } from './WeekNavigator';
import CopyWeekConfirmModal from './CopyWeekConfirmModal';

const MAX_REPEAT_COUNT = 12;

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function getMondayOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseYMD(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addWeeks(date, weeks) {
  const d = new Date(date);
  d.setDate(d.getDate() + weeks * 7);
  return d;
}

function CopyWeekModal({ open, sourceWeekStart, onClose, onSuccess }) {
  const { t } = useTranslation('scheduler');

  const [targetWeekStart, setTargetWeekStart] = useState(null);
  const [repeatCount, setRepeatCount] = useState(1);
  const [inFlight, setInFlight] = useState(false);
  const [error, setError] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTargetWeekStart(getMondayOf(addWeeks(sourceWeekStart, 1)));
    setRepeatCount(1);
    setInFlight(false);
    setError(null);
    setConfirmState(null);
  }, [open, sourceWeekStart]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !inFlight) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, inFlight, onClose]);

  if (!open || !targetWeekStart) return null;

  const targetWeeks = Array.from({ length: repeatCount }, (_, i) => addWeeks(targetWeekStart, i));
  const overlapsSource = targetWeeks.some((d) => toYMD(d) === toYMD(sourceWeekStart));

  const handleTargetDateChange = (e) => {
    if (!e.target.value) return;
    setTargetWeekStart(getMondayOf(parseYMD(e.target.value)));
    setError(null);
  };

  const handleRepeatCountChange = (e) => {
    const n = Number(e.target.value);
    if (Number.isNaN(n)) return;
    setRepeatCount(Math.min(MAX_REPEAT_COUNT, Math.max(1, n)));
    setError(null);
  };

  const submit = async (overwrite) => {
    setInFlight(true);
    setError(null);
    try {
      const res = await apiClient.post('/api/schedules/copy-week', {
        sourceWeekStart: toYMD(sourceWeekStart),
        targetWeekStart: toYMD(targetWeekStart),
        repeatCount,
        overwrite,
      });
      const data = res.data;
      if (data.written) {
        setConfirmState(null);
        onSuccess(data);
      } else if (data.blockReason === 'DRAFT_CONFLICT') {
        const collisionWeeks = data.targets
          .filter((tg) => tg.status === 'DRAFT_COLLISION')
          .map((tg) => tg.weekStart);
        setConfirmState({ collisionWeeks });
      } else if (data.blockReason === 'PUBLISHED') {
        const weeks = data.targets
          .filter((tg) => tg.status === 'PUBLISHED_BLOCKED')
          .map((tg) => tg.weekStart);
        setConfirmState(null);
        setError({ type: 'published', weeks });
      } else {
        setConfirmState(null);
        setError({ type: 'generic' });
      }
    } catch (err) {
      setConfirmState(null);
      if (err.response?.status === 404) {
        setError({ type: 'sourceEmpty' });
      } else {
        setError({ type: 'generic', message: err.response?.data?.message });
      }
    } finally {
      setInFlight(false);
    }
  };

  const isValid = !overlapsSource && repeatCount >= 1 && repeatCount <= MAX_REPEAT_COUNT;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="copy-week-title"
      >
        <div className="w-full max-w-md max-h-[90vh] bg-white rounded-2xl shadow-2xl border border-orange-100 flex flex-col overflow-hidden">
          <div className="shrink-0 flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
            <h2 id="copy-week-title" className="text-lg font-semibold text-gray-900">
              {t('copyWeek.modal.title')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={inFlight}
              className="ml-4 text-gray-400 hover:text-gray-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-200 rounded"
              aria-label={t('copyWeek.modal.closeLabel')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('copyWeek.modal.sourceLabel')}
              </span>
              <p className="text-sm text-gray-900 font-semibold">
                {formatWeekRange(sourceWeekStart)}
              </p>
            </div>

            <div>
              <label htmlFor="cw-target" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('copyWeek.modal.targetLabel')}
              </label>
              <input
                id="cw-target"
                type="date"
                value={toYMD(targetWeekStart)}
                onChange={handleTargetDateChange}
                disabled={inFlight}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500 disabled:opacity-60"
              />
              <p className="mt-1.5 text-xs text-gray-500">{t('copyWeek.modal.targetHint')}</p>
            </div>

            <div>
              <label htmlFor="cw-repeat" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('copyWeek.modal.repeatLabel')}
              </label>
              <input
                id="cw-repeat"
                type="number"
                min={1}
                max={MAX_REPEAT_COUNT}
                value={repeatCount}
                onChange={handleRepeatCountChange}
                disabled={inFlight}
                className="w-28 px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500 disabled:opacity-60"
              />
              <p className="mt-1.5 text-xs text-gray-500">{t('copyWeek.modal.repeatHint')}</p>
            </div>

            {repeatCount > 1 && (
              <p className="text-xs text-gray-500">
                {t('copyWeek.modal.weeksList', {
                  list: targetWeeks.map((d) => formatWeekRange(d)).join(', '),
                })}
              </p>
            )}

            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
              {t('copyWeek.modal.draftNotice')}
            </p>

            {overlapsSource && (
              <p className="text-xs text-red-600" role="alert">{t('copyWeek.modal.errorSourceOverlap')}</p>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
                {error.type === 'published' && (
                  <>
                    <p>{t('copyWeek.errors.published')}</p>
                    <ul className="mt-1.5 list-disc list-inside space-y-0.5">
                      {error.weeks.map((ws) => (
                        <li key={ws}>{formatWeekRange(parseYMD(ws))}</li>
                      ))}
                    </ul>
                  </>
                )}
                {error.type === 'sourceEmpty' && <p>{t('copyWeek.errors.sourceEmpty')}</p>}
                {error.type === 'generic' && <p>{error.message || t('copyWeek.errors.generic')}</p>}
              </div>
            )}
          </div>

          <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={inFlight}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              {t('copyWeek.modal.cancel')}
            </button>
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={!isValid || inFlight}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {inFlight && <Spinner />}
              {inFlight ? t('copyWeek.modal.copying') : t('copyWeek.modal.copy')}
            </button>
          </div>
        </div>
      </div>

      <CopyWeekConfirmModal
        open={!!confirmState}
        collisionWeeks={confirmState?.collisionWeeks}
        inFlight={inFlight}
        onReplace={() => submit(true)}
        onCancel={() => setConfirmState(null)}
      />
    </>
  );
}

export default CopyWeekModal;
